import { PublicClientApplication, type Configuration } from '@azure/msal-browser';
import { config } from '../config';

// MSAL is only needed for Private forms. When no client id is configured (e.g. a
// public-only deployment) it stays disabled and the public/secret paths still work.
export const msalEnabled = Boolean(config.entra.clientId);

const msalConfig: Configuration = {
  auth: {
    clientId: config.entra.clientId,
    authority: `https://login.microsoftonline.com/${config.entra.tenantId}`,
    redirectUri: window.location.origin,
  },
  cache: { cacheLocation: 'localStorage' },
};

export const msalInstance = msalEnabled ? new PublicClientApplication(msalConfig) : null;

const apiScopes = config.entra.apiScope ? [config.entra.apiScope] : [];
export const loginRequest = { scopes: ['openid', 'profile', ...apiScopes] };

function activeAccount() {
  return msalInstance?.getActiveAccount() ?? msalInstance?.getAllAccounts()[0] ?? null;
}

export function isSignedIn(): boolean {
  return activeAccount() != null;
}

/** Initialises MSAL and processes any redirect return. Call once at startup. */
export async function initAuth(): Promise<void> {
  if (!msalInstance) {
    return;
  }
  await msalInstance.initialize();
  await msalInstance.handleRedirectPromise();
  const account = activeAccount();
  if (account) {
    msalInstance.setActiveAccount(account);
  }
}

/** An API access token if the respondent is signed in, else null (silent → redirect fallback). */
export async function getAccessTokenOrNull(): Promise<string | null> {
  const account = activeAccount();
  if (!msalInstance || !account) {
    return null;
  }
  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account });
    return result.accessToken;
  } catch {
    await msalInstance.acquireTokenRedirect({ scopes: apiScopes, account });
    return null;
  }
}

/** Starts interactive sign-in (full-page redirect). */
export async function signIn(): Promise<void> {
  await msalInstance?.loginRedirect(loginRequest);
}
